import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { analytics } from "@/lib/analytics";
import { Shield, Flag, CircleDot, Car, Trophy } from "lucide-react";

const TEAMS = [
  { name: "McLaren", colour: "FF8000" },
  { name: "Ferrari", colour: "E8002D" },
  { name: "Red Bull Racing", colour: "3671C6" },
  { name: "Mercedes", colour: "27F4D2" },
  { name: "Aston Martin", colour: "229971" },
  { name: "Alpine", colour: "FF87BC" },
  { name: "Williams", colour: "64C4FF" },
  { name: "Racing Bulls", colour: "6692FF" },
  { name: "Haas", colour: "B6BABD" },
  { name: "Audi", colour: "B82100" },
  { name: "Cadillac", colour: "C0C0C0" },
] as const;

const AVATARS = [
  { id: "helmet", label: "Helmet", Icon: Shield },
  { id: "flag", label: "Flag", Icon: Flag },
  { id: "wheel", label: "Wheel", Icon: CircleDot },
  { id: "car", label: "Car", Icon: Car },
  { id: "trophy", label: "Trophy", Icon: Trophy },
] as const;

function F1Lights({ activeLights }: { activeLights: number }) {
  return (
    <div className="flex justify-center gap-3 py-2">
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className={cn(
            "h-6 w-6 rounded-full border-2 border-border transition-all duration-500",
            i < activeLights
              ? "bg-red-600 border-red-700 shadow-[0_0_12px_rgba(220,38,38,0.6)]"
              : "bg-muted"
          )}
          style={{ transitionDelay: i < activeLights ? "0ms" : "0ms" }}
        />
      ))}
    </div>
  );
}

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, profile, refetchProfile } = useAuth();
  const [step, setStep] = useState(1);
  const [displayName, setDisplayName] = useState("");
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null);
  const [selectedAvatar, setSelectedAvatar] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [activeLights, setActiveLights] = useState(0);

  useEffect(() => {
    if (profile?.display_name) {
      setDisplayName(profile.display_name);
    }
  }, [profile]);

  // Animate lights when step advances
  useEffect(() => {
    if (step === 1) {
      setActiveLights(0);
    }
  }, []);

  const advanceToStep2 = () => {
    setActiveLights(1);
    setStep(2);
  };

  const advanceToStep3 = () => {
    setActiveLights(2);
    setTimeout(() => setActiveLights(3), 300);
    setStep(3);
  };

  const goBackToStep1 = () => {
    setActiveLights(0);
    setStep(1);
  };

  const goBackToStep2 = () => {
    setActiveLights(1);
    setStep(2);
  };

  const teamColour = TEAMS.find((t) => t.name === selectedTeam)?.colour;

  const handleComplete = async () => {
    if (!selectedTeam || !selectedAvatar || !displayName.trim()) return;
    if (!user) {
      toast({ title: "Not signed in", description: "Please sign in first.", variant: "destructive" });
      navigate("/sign-in", { replace: true });
      return;
    }
    // Light up 4th and 5th lights
    setActiveLights(4);
    setTimeout(() => setActiveLights(5), 300);

    setSaving(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          display_name: displayName.trim(),
          favourite_team: selectedTeam,
          team_colour: teamColour!,
          avatar_icon: selectedAvatar,
          onboarding_completed: true,
        })
        .eq("id", user.id);

      if (error) {
        setSaving(false);
        setActiveLights(3);
        toast({ title: "Failed to save profile", description: error.message, variant: "destructive" });
      } else {
        analytics.track("onboarding_completed", {
          favourite_team: selectedTeam,
          avatar_icon: selectedAvatar,
        });
        await refetchProfile();
        navigate("/", { replace: true });
      }
    } catch (err) {
      console.error("Onboarding error:", err);
      setSaving(false);
      setActiveLights(3);
      toast({ title: "Something went wrong", description: "Please try again.", variant: "destructive" });
    }
  };

  const canAdvance =
    (step === 1 && displayName.trim().length > 0) ||
    (step === 2 && selectedTeam !== null) ||
    (step === 3 && selectedAvatar !== null);

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-8">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center space-y-3">
          <CardTitle className="text-2xl font-black tracking-tight text-primary">SET UP YOUR PROFILE</CardTitle>
          <F1Lights activeLights={activeLights} />
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Step 1: Display Name */}
          {step === 1 && (
            <div className="space-y-4 animate-fade-in">
              <div className="space-y-2">
                <Label htmlFor="displayName">Display Name</Label>
                <Input
                  id="displayName"
                  placeholder="Your nickname"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value.slice(0, 20))}
                  maxLength={20}
                  required
                />
                <p className="text-xs text-muted-foreground">{displayName.length}/20 characters</p>
              </div>
            </div>
          )}

          {/* Step 2: Favourite Team */}
          {step === 2 && (
            <div className="space-y-3 animate-fade-in">
              <Label>Pick your team</Label>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {TEAMS.map((team) => {
                  const isSelected = selectedTeam === team.name;
                  return (
                    <button
                      key={team.name}
                      type="button"
                      onClick={() => setSelectedTeam(team.name)}
                      className={cn(
                        "flex flex-col items-center gap-1.5 rounded-lg border-2 p-3 text-sm font-semibold transition-all",
                        isSelected ? "scale-[1.03] shadow-md" : "border-border hover:border-muted-foreground/40"
                      )}
                      style={isSelected ? { borderColor: `#${team.colour}`, backgroundColor: `#${team.colour}18` } : undefined}
                    >
                      <span
                        className="h-6 w-6 rounded-full"
                        style={{ backgroundColor: `#${team.colour}` }}
                      />
                      {team.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 3: Avatar */}
          {step === 3 && (
            <div className="space-y-3 animate-fade-in">
              <Label>Choose an avatar</Label>
              <div className="flex flex-wrap justify-center gap-4">
                {AVATARS.map(({ id, label, Icon }) => {
                  const isSelected = selectedAvatar === id;
                  const ringColor = teamColour ? `#${teamColour}` : "hsl(var(--primary))";
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setSelectedAvatar(id)}
                      className={cn(
                        "flex flex-col items-center gap-1.5 rounded-xl border-2 p-4 transition-all",
                        isSelected ? "scale-[1.08] shadow-lg" : "border-border hover:border-muted-foreground/40"
                      )}
                      style={isSelected ? { borderColor: ringColor, backgroundColor: `${ringColor}18` } : undefined}
                    >
                      <div
                        className="flex h-14 w-14 items-center justify-center rounded-full"
                        style={isSelected ? { backgroundColor: `${ringColor}25` } : { backgroundColor: "hsl(var(--muted))" }}
                      >
                        <Icon size={28} style={isSelected ? { color: ringColor } : undefined} />
                      </div>
                      <span className="text-xs font-medium">{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex gap-3">
            {step === 2 && (
              <Button variant="outline" className="flex-1" onClick={goBackToStep1}>
                Back
              </Button>
            )}
            {step === 3 && (
              <Button variant="outline" className="flex-1" onClick={goBackToStep2}>
                Back
              </Button>
            )}
            {step === 1 && (
              <Button className="flex-1 font-semibold" disabled={!canAdvance} onClick={advanceToStep2}>
                Next
              </Button>
            )}
            {step === 2 && (
              <Button className="flex-1 font-semibold" disabled={!canAdvance} onClick={advanceToStep3}>
                Next
              </Button>
            )}
            {step === 3 && (
              <Button className="flex-1 font-semibold" disabled={!canAdvance || saving} onClick={handleComplete}>
                {saving ? "Saving…" : "Send It 🏁"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
